import { ReactNode } from 'react';
import { FaCircleInfo } from 'react-icons/fa6';
import { useSelector } from 'react-redux';

import { Box, CircularProgress, Typography } from '@mui/material';

import { ReactComponent as EURIcon } from 'assets/icons/euro.svg';
import { ReactComponent as USDIcon } from 'assets/icons/usdSign.svg';
import { TOOLTIP_MESSAGES } from 'constants/notifications';
import { TooltipIcon } from 'shared/TooltipIcon';
import { useTheme } from 'themes/themeProvider';
import { Currency } from 'types/general.types';

import { getBalance } from './options';

import { userDataSelector } from 'store/slices/user-slice';
import classes from './BalanceCard.module.scss';

type Props = {
  actionElement?: ReactNode;
  testId?: string;
  isLoading?: boolean;
};

export const FiatBalanceCard = ({ actionElement, isLoading, testId = 'fiat-balance-card' }: Props) => {
  const { theme } = useTheme();

  const { balance = 0, deposit = 0, baseCurrency = Currency.USD } = useSelector(userDataSelector) ?? {};

  return (
    <Box data-testid={testId} height='100%' minHeight={210} display='flex' flexDirection='column'>
      {isLoading ? (
        <div className={classes.loaderContainer}>
          <CircularProgress className={classes.loader} size={24} sx={{ color: theme.widgets.fiatWallet.textColor }} />
        </div>
      ) : (
        <>
          <Box display='flex' alignItems='center'>
            {baseCurrency === Currency.USD ? <USDIcon /> : <EURIcon />}
            <Typography variant='caption' display='block' ml={1}>
              {baseCurrency} Account
            </Typography>
          </Box>

          <Box display='flex' flexDirection='column' my={1} mt={2}>
            <Box display='flex' alignItems='center'>
              <Typography variant='caption' mr={1}>
                Active balance
              </Typography>
              <TooltipIcon
                icon={<FaCircleInfo color={theme.widgets.bitcoinWallet.textColor} />}
                tooltipId='Active balance'
                tooltipText={TOOLTIP_MESSAGES.ACTIVE_BALANCE}
              />
            </Box>
            <Typography variant='h3'>{getBalance('1', balance, baseCurrency)}</Typography>
          </Box>

          <Box display='flex' flexDirection='column' my={1}>
            <Box display='flex' alignItems='center'>
              <Typography variant='caption' mr={1}>
                Blocked deposit
              </Typography>
              <TooltipIcon
                icon={<FaCircleInfo color={theme.widgets.bitcoinWallet.textColor} />}
                tooltipId='Blocked deposit'
                tooltipText={TOOLTIP_MESSAGES.BLOCKED_DEPOSIT}
              />
            </Box>
            <Typography variant='h3'>{getBalance('1', deposit, baseCurrency)}</Typography>
          </Box>

          <Box display='flex' justifyContent='center' alignItems='center' mt='auto'>
            {actionElement}
          </Box>
        </>
      )}
    </Box>
  );
};
